import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  DestinatarioNFCeDto,
  MeioPagamentoNFCeDto,
  ProdutoConsultaDto,
  ProdutoConsultaSchema,
  ProdutoNFCeDto,
  NFCeDto,
} from "./NFCe.Dto";
import { ProdutoFiltrar } from "src/services/Estoque.Service";
import {
  ExtrairConsultaProdutoComando,
  MapearErrosServidor,
} from "src/hook/Functions";
import {
  NFCeBuscarUltima,
  NFCeEnviar,
  NFCeIncluir,
} from "src/services/Fiscal.Service";
import NFCeMenuCode from "./NFCe.Menu.Code";
import NFCeConfiguracaoCode from "./NFCe.Configuracao.Code";
import { TipoConfiguracaoEnum } from "../../Configuracoes/Sistema/Sistema.Dto";
import { NFCeCodeLoad } from "./NFCe.Code.Load";
import { showToast } from "src/componentes";

const NFCeCode = () => {
  const [dataProdutoConsulta, setDataProdutoConsulta] = useState<
    ProdutoNFCeDto[]
  >([]);
  const nfceCodeLoad = NFCeCodeLoad();
  const [modalShowConsultaProduto, setModalShowConsultaProduto] =
    useState(false);
  const [modalShowFinaliza, setModalShowFinaliza] = useState(false);
  const [descricaoProdutoConsulta, setDescricaoProdutoConsulta] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataMeioPagamento, setDataMeioPagamento] = useState<
    MeioPagamentoNFCeDto[]
  >([]);
  const [modalShowPropriedades, setModalShowPropriedades] = useState(false);
  const [produtoPropriedades, setProdutoPropriedades] =
    useState<ProdutoNFCeDto>();

  const [nfceId, setNfceId] = useState(0);

  const formProdutoConsulta = useForm<ProdutoConsultaDto>({
    resolver: yupResolver(ProdutoConsultaSchema),
  });

  const menu = NFCeMenuCode({ nfceId, setDescricaoProdutoConsulta });

  const configuracao = NFCeConfiguracaoCode();
  const serie = Number(
    configuracao.getConfiguracaoValor(TipoConfiguracaoEnum.Serie)
  );

  useEffect(() => {
    if (configuracao.loading) return;
    if (!serie) return;

    const controller = new AbortController();
    const Load = async () => {
      formProdutoConsulta.setFocus("nome", { shouldSelect: true });
      formProdutoConsulta.setValue("nome", "");
      setLoading(true);

      await NFCeBuscarUltima(serie)
        .then((response) => {
          if (
            response.data.data.codigoStatusEnvio !== 2 &&
            response.data.data.codigoStatusEnvio !== 3
          ) {
            showToast({
              message: "Existe NFCe em aberto não enviadas",
              title: "Atenção",
              variant: "Warning",
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    };
    Load();
    return () => {
      controller.abort();
    };
  }, [configuracao.loading, serie]);

  const handleClickPropriedades = (value: ProdutoNFCeDto) => () => {
    setModalShowPropriedades(true);
    setProdutoPropriedades(value);
  };

  const onCancelModalPropriedades = () => {
    setModalShowPropriedades(false);
  };

  const onDeleteModalPropriedades = (value: ProdutoNFCeDto) => {
    if (nfceCodeLoad.dataProdutoNFCe.length === 1) {
      localStorage.setItem(nfceCodeLoad.CACHE_KEY, JSON.stringify([]));
      nfceCodeLoad.setDataProdutoNFCe([]);
    } else {
      nfceCodeLoad.setDataProdutoNFCe((prev) =>
        prev.filter((item) => item !== value)
      );
    }
  };

  const onSubmitProdutoConsulta = async (data: ProdutoConsultaDto) => {
    setLoading(true);

    const comando = ExtrairConsultaProdutoComando(data.nome);
    if (comando) {
      data.nome = comando.codigoProduto;
    }

    const response = await ProdutoFiltrar(data);
    if (response.data.success) {
      const data = response.data.data;

      data.forEach((item: ProdutoNFCeDto) => {
        item.valorUnitarioComercial =
          comando?.valor ?? item.valorUnitarioComercial;
        item.valorUnitarioTributavel = item.valorUnitarioComercial;
        item.quantidadeComercial = comando?.quantidade ?? 1;
        item.quantidadeTributavel = comando?.quantidade ?? 1;

        item.codigoBarrasTributavel = item.codigoBarras;
        item.unidadeTributavel = item.unidadeComercial;
        item.quantidadeTributavel = item.quantidadeComercial;

        item.indicaTotal = 1;
      });

      if (data.length === 0) {
        formProdutoConsulta.setError(
          "nome",
          { message: "Não localizado" },
          { shouldFocus: true }
        );
      } else if (data.length === 1) {
        setDescricaoProdutoConsulta(data[0].nome);
        const produtoNFCe = data[0] as ProdutoNFCeDto;
        nfceCodeLoad.setDataProdutoNFCe((prevData) => {
          const nextSeq = prevData.length + 1;
          return [
            ...prevData,
            {
              ...produtoNFCe,
              sequencia: produtoNFCe.sequencia || nextSeq,
              index: nextSeq,
            },
          ];
        });
        formProdutoConsulta.setValue("nome", "");
        setTimeout(() => {
          formProdutoConsulta.setFocus("nome", { shouldSelect: true });
        }, 100);
      } else {
        setDataProdutoConsulta(data);
        setModalShowConsultaProduto(true);
      }
    } else {
      MapearErrosServidor(response.data.notifications, showToast);
    }
    setLoading(false);
  };

  const onSubmitNFCe = async (value: DestinatarioNFCeDto | undefined) => {
    setLoading(true);
    const numeroAlterado = localStorage.getItem("nfceAlterarNumero");
    const nfce: NFCeDto = {
      serie: configuracao.getConfiguracaoValor(TipoConfiguracaoEnum.Serie),
      numeroNF:
        numeroAlterado && !isNaN(Number(numeroAlterado))
          ? Number(numeroAlterado)
          : 0,
      destinatario: value,
      itens: nfceCodeLoad.dataProdutoNFCe,
      pagamento: dataMeioPagamento,
    };
    localStorage.removeItem("nfceAlterarNumero");
    showToast({
      message: "Inserindo...",
      title: "Em cadastro",
      variant: "Info",
    });
    const response = await NFCeIncluir(nfce);
    if (response.data.success) {
      showToast({
        message: "Aguarde...",
        title: "Enviando",
        variant: "Info",
      });
      const responseEnviar = await NFCeEnviar(response.data.data.id);
      if (responseEnviar.data.success) {
        showToast({
          message: responseEnviar.data.notifications[0].message,
          title: "Enviada",
          variant: "Primary",
        });
        if (
          responseEnviar.data.data?.codigoStatus &&
          responseEnviar.data.data?.codigoStatus === 100
        ) {
          menu.imprimirDanfe(response.data.data.id);
          setNfceId(response.data.data.id);
          localStorage.setItem(nfceCodeLoad.CACHE_KEY, JSON.stringify([]));
          nfceCodeLoad.setDataProdutoNFCe([]);
          setDataMeioPagamento([]);
        }
      } else {
        response.data.notifications.forEach((item) => {
          showToast({
            message: item.message,
            title: "Erro",
            variant: "Danger",
          });
        });
      }
    } else {
      response.data.notifications.forEach((item) => {
        showToast({
          message: item.key + ": " + item.message,
          title: "Erro",
          variant: "Danger",
        });
      });
    }
    setLoading(false);
  };

  const onHideModalConsultaProduto = () => {
    setModalShowConsultaProduto(false);
    setTimeout(() => {
      formProdutoConsulta.setFocus("nome", { shouldSelect: true });
    }, 100);
  };

  const onHideModalFinaliza = () => {
    setModalShowFinaliza(false);
  };

  const onSelectModalConsultaProduto = (value: ProdutoNFCeDto) => {
    onHideModalConsultaProduto();
    setDescricaoProdutoConsulta(value.nome);
    nfceCodeLoad.setDataProdutoNFCe((prevData) => {
      const nextSeq = prevData.length + 1;
      return [
        ...prevData,
        {
          ...value,
          sequencia: value.sequencia || nextSeq,
          index: nextSeq,
        },
      ];
    });
    formProdutoConsulta.setValue("nome", "");
    setTimeout(() => {
      formProdutoConsulta.setFocus("nome", { shouldSelect: true });
    }, 150);
  };

  const onSelectModalFinaliza = (value: MeioPagamentoNFCeDto) => {
    setDataMeioPagamento((prevData) => [...prevData, value]);
  };

  const totalNFCe = () => {
    const total = nfceCodeLoad.dataProdutoNFCe.reduce(
      (acc, item) =>
        acc + item.valorUnitarioComercial * item.quantidadeComercial,
      0
    );
    return total;
  };

  const possuiItens = () => {
    return nfceCodeLoad.dataProdutoNFCe.length > 0;
  };

  const onCancelModalFinaliza = () => {
    setDataMeioPagamento([]);
  };

  return {
    loading,
    onSubmitProdutoConsulta,
    modalShowConsultaProduto,
    dataProdutoConsulta,
    onHideModalConsultaProduto,
    onSelectModalConsultaProduto,
    onCancelModalFinaliza,
    totalNFCe,
    descricaoProdutoConsulta,
    possuiItens,
    modalShowFinaliza,
    setModalShowFinaliza,
    dataMeioPagamento,
    onSelectModalFinaliza,
    onHideModalFinaliza,
    onSubmitNFCe,
    modalShowPropriedades,
    setModalShowPropriedades,
    handleClickPropriedades,
    produtoPropriedades,
    onCancelModalPropriedades,
    onDeleteModalPropriedades,
    formProdutoConsulta,
    menu,
    configuracoes: configuracao,
    nfceCodeLoad,
  };
};
export default NFCeCode;
